<script>
    import ApiClient, { SessionExpiredError } from './scripts/apiclient';
    import { keyScope } from './scripts/keyboard';

    /**
     * The shortest query worth sending. The server refuses anything shorter, and so do the
     * apps and the web app, so a shorter query is answered here rather than asked about.
     */
    const minQueryLength = 3;

    /**
     * Whether the search is out of the way. A playground being put in place and playgrounds
     * being picked out to merge own the whole map: panning away from what is being worked on
     * is no help, so the field steps aside until the task is over. The query is kept rather
     * than thrown away, so that coming back out lands on the search that led there.
     */
    export let hidden = false;

    /**
     * Whether a dialog is open on the map. The field stays up beside one where there is room
     * for both, so that the next playground can be looked up without closing the one in
     * hand, and dialogs are panned clear of the strip it sits in. On a narrow screen a
     * dialog is nearly as wide as the screen and there is no room, which the media query
     * below settles.
     */
    export let dialogOpen = false;

    /**
     * Where to search around, as a function returning a location with lat and lon. Results
     * are sorted by their distance from there, so a name that exists in several places
     * finds the one nearest what is being looked at.
     */
    export let getCenter;

    /**
     * Called with the location of a place when one is picked.
     */
    export let selectPlace;

    /**
     * Called with the id of a playground when one is picked.
     */
    export let selectPlayground;

    /** The country names are read in Swedish, like the rest of the interface. */
    const countryNames = makeCountryNames();

    let root;
    let input;

    let query = '';

    /** The results of the last search, or an empty list while there are none to show. */
    let results = [];

    /** What to say instead of results: nothing found, too short a query, a failed request. */
    let message = null;

    /**
     * The country the location searched around belongs to, as reported by the server. The
     * results from it are the expected ones and go without a heading; the rest are grouped
     * under the country they are in.
     */
    let userCountry = null;

    /** Whether the results are on show. */
    let open = false;

    /** Whether a request is on its way. */
    let searching = false;

    /**
     * The number of the last search started. A search that is no longer the last one when it
     * comes back is dropped: two searches started in a row can be answered in the other
     * order, and the results of the abandoned one would otherwise replace the ones being
     * read.
     */
    let searchNumber = 0;

    // The results collapse along with the field, and when a dialog opens: only the strip
    // the field sits in is kept clear of dialogs, and an open list reaches far further down
    // than that. Focusing the field brings them back, and a search made while a dialog is
    // open still opens its results, since this only runs when one of these two changes.
    $: if (hidden || dialogOpen) {
        open = false;
    }

    $: rows = groupByCountry(results, userCountry);

    /**
     * Runs the search for whatever is in the field.
     */
    async function search() {
        let text = query.trim();

        if (text.length < minQueryLength) {
            return show([], 'Ange minst tre tecken');
        }

        let center = getCenter();

        if (!center) {
            return;
        }

        let number = ++searchNumber;
        searching = true;

        let response;

        try {
            response = await ApiClient.searchPlaces(text, center.lat, center.lon);
        } catch (err) {
            console.log('The search failed', err);

            if (number !== searchNumber) {
                return;
            }

            searching = false;

            // An expired session shows the login form, and reporting it here as a failed
            // search would only be misleading.
            if (!(err instanceof SessionExpiredError)) {
                show([], 'Sökningen misslyckades. Försök igen.');
            }

            return;
        }

        if (number !== searchNumber) {
            return;
        }

        searching = false;
        userCountry = response.userCountry || null;

        let found = response.results || [];

        if (found.length === 0) {
            return show([], 'Inga resultat');
        }

        // A single result is what was being looked for, so go there rather than asking which
        // of the one it is.
        if (found.length === 1) {
            return select(found[0]);
        }

        show(found, null);
    }

    /**
     * Puts either a set of results or a message in the list, and opens it.
     *
     * @param {Array} found The results to show.
     * @param {String} text The message to show instead, if there are no results.
     */
    function show(found, text) {
        results = found;
        message = text;
        open = true;
    }

    /**
     * Collapses the list without forgetting it, so that focusing the field again brings the
     * same results back rather than an empty list.
     */
    function collapse() {
        open = false;
    }

    /**
     * Empties the list. Used once a result has been picked, where there is nothing left to
     * come back to.
     */
    function reset() {
        results = [];
        message = null;
        open = false;
    }

    /**
     * Goes to a result and puts the list away.
     *
     * @param {Object} result The result that was picked.
     */
    function select(result) {
        reset();
        input.blur();

        if (isPlayground(result)) {
            return selectPlayground(result._id);
        }

        let coordinates = result.location ? result.location.coordinates : null;

        if (!coordinates) {
            return;
        }

        selectPlace({ lat: coordinates[1], lon: coordinates[0] });
    }

    function clear() {
        query = '';
        reset();
        input.focus();
    }

    function keyDown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            search();
        }
    }

    /**
     * Puts the results away and lets go of the field, so that a second Escape reaches the
     * map behind it.
     */
    function escape() {
        collapse();
        input.blur();
    }

    /**
     * The shortcuts of the search.
     *
     * Escape can't be handled on the field itself: the app handles keys in the capture
     * phase and stops the ones a scope has bound, so a key the map has claimed never
     * reaches whatever has the focus. It is bound here only while there are results up to
     * close, so that Escape still ends a multiple selection when there aren't.
     */
    const shortcuts = {};

    $: shortcuts.Escape = open ? escape : undefined;

    keyScope(shortcuts);

    /**
     * Brings the last results back when the field is focused again. A message is left out:
     * being told that a query was too short belongs to the moment it was searched for.
     */
    function focus() {
        if (results.length > 0) {
            open = true;
        }
    }

    /**
     * Puts the list away when something outside it is pressed, the map included.
     */
    function pressedOutside(event) {
        if (root && !root.contains(event.target)) {
            collapse();
        }
    }

    function isPlayground(result) {
        return typeOf(result) === 'playground';
    }

    function typeOf(result) {
        return (result.type || '').toLowerCase();
    }

    /**
     * The icon standing for a result. Font Awesome has no swing, so a playground is a child
     * and an indoor one is the building it is in.
     *
     * @param {Object} result The result to picture.
     * @returns {String} The classes of the icon.
     */
    function iconFor(result) {
        if (isPlayground(result)) {
            return result.subtype === 'indoors' ? 'fas fa-warehouse' : 'fas fa-child';
        }

        if (typeOf(result) === 'city') {
            return 'fas fa-city';
        }

        return 'fas fa-map-marker-alt';
    }

    /**
     * Works out which results open a new country, so that a list reaching across borders
     * says where each part of it is. A result in the same country as the location searched
     * around needs no heading: that is where the search was expected to land.
     *
     * @param {Array} found The results to go through.
     * @param {String} home The country the location searched around belongs to.
     * @returns {Array} The results, each with the heading to put above it or null.
     */
    function groupByCountry(found, home) {
        let previous = null;

        return found.map((result) => {
            let country = result.country || null;
            let heading = null;

            if (country && !sameCountry(country, previous) && !sameCountry(country, home)) {
                heading = country;
            }

            previous = country;

            return { result: result, heading: heading };
        });
    }

    function sameCountry(one, other) {
        return (one || '').toLowerCase() === (other || '').toLowerCase();
    }

    /**
     * Turns an ISO 3166-1 alpha-2 country code into its flag, e.g. "se" into 🇸🇪. The flags
     * are two regional indicator letters, which sit a fixed distance above A to Z.
     *
     * @param {String} code The country code.
     * @returns {String} The flag, or an empty string for anything that isn't a code.
     */
    function countryFlag(code) {
        let letters = (code || '').toUpperCase();

        if (!/^[A-Z]{2}$/.test(letters)) {
            return '';
        }

        let firstIndicator = 0x1f1e6 - 'A'.charCodeAt(0);

        return String.fromCodePoint(firstIndicator + letters.charCodeAt(0), firstIndicator + letters.charCodeAt(1));
    }

    function countryName(code) {
        if (!countryNames) {
            return code;
        }

        try {
            return countryNames.of((code || '').toUpperCase()) || code;
        } catch (err) {
            return code;
        }
    }

    function makeCountryNames() {
        try {
            return new Intl.DisplayNames(['sv'], { type: 'region' });
        } catch (err) {
            console.log('Country names are not available', err);
            return null;
        }
    }
</script>

<svelte:window on:pointerdown={pressedOutside} />

<div class="search-box" class:hidden class:dialog-open={dialogOpen} bind:this={root}>
    <div class="field">
        <button class="icon-button" type="button" title="Sök" on:click={search}>
            {#if searching}
                <span class="spinner-border spinner-border-sm" role="status" />
            {:else}
                <i class="fas fa-search" />
            {/if}
        </button>

        <!-- svelte-ignore a11y-autocomplete-valid -->
        <input
            type="search"
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            enterkeyhint="search"
            placeholder="Sök ort eller lekplats"
            aria-label="Sök ort eller lekplats"
            bind:value={query}
            bind:this={input}
            on:keydown={keyDown}
            on:focus={focus}
        />

        {#if query}
            <button class="icon-button" type="button" title="Rensa sökningen" on:click={clear}>
                <i class="fas fa-times" />
            </button>
        {/if}
    </div>

    {#if open}
        <div class="results">
            {#if message}
                <p class="message">{message}</p>
            {:else}
                {#each rows as row}
                    {#if row.heading}
                        <p class="country">
                            <span class="flag">{countryFlag(row.heading)}</span>
                            {countryName(row.heading)}
                        </p>
                    {/if}

                    <button class="result" type="button" on:click={() => select(row.result)}>
                        <i class="{iconFor(row.result)} result-icon" />
                        <span class="result-text">
                            <span class="name">{row.result.name}</span>
                            {#if row.result.municipality}
                                <span class="municipality">{row.result.municipality}</span>
                            {/if}
                        </span>
                    </button>
                {/each}
            {/if}
        </div>
    {/if}
</div>

<style>
    /* Sits in the top right corner of the map, which Leaflet leaves empty: the zoom buttons
       are in the top left and the attribution is turned off. The map pane of the shell is
       what the edges are measured against.

       The left edge starts past the zoom buttons, which reach 44px in from the left, rather
       than the field being pinned to a width. On a narrow screen there is no room for the
       360px it would like, and it gives up its own width instead of the buttons having to
       move aside for it. The auto margin is what holds it to the right as it shrinks.

       Above 1000, which is where Leaflet holds the corners its controls sit in. The map
       container sets neither position nor z-index, so it opens no stacking context of its
       own and the controls inside it are ordered against this box directly. At the same
       z-index the later one in the document wins, and that is the map, which left the
       controls drawing on top of the results. */
    .search-box {
        position: absolute;
        top: 10px;
        left: 56px;
        right: 10px;
        max-width: 360px;
        margin-left: auto;
        z-index: 1100;
    }

    /* Kept in place rather than taken out, so that a dialog opening and closing doesn't
       empty the field. */
    .search-box.hidden {
        visibility: hidden;
    }

    /* The breakpoint of the shell, where the inbox and the map stop sharing the screen and
       a dialog grows to nearly the whole width of it. Above it a dialog and the field are
       both up at once, and Map.svelte keeps dialogs out of this corner. */
    @media (max-width: 991.98px) {
        .search-box.dialog-open {
            visibility: hidden;
        }
    }

    .field {
        display: flex;
        align-items: center;
        height: 44px;
        padding: 0 4px;
        background: #fff;
        border: 1px solid #444;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    }

    /* The margin is the half em that global.css gives every button and field, to space
       them down a form. A flex row centres its items by the margin box, so that bottom
       margin lifted everything in the field four pixels above the middle. The toolbar and
       the bar above the map drop it the same way.

       The glyph is centred by the box rather than left sitting on the text baseline, so
       that each icon lands in the same place whatever its own height is. */
    .icon-button {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        margin: 0;
        padding: 0;
        border: none;
        background: none;
        color: #555;
        cursor: pointer;
    }

    .icon-button:hover {
        color: #000;
    }

    /* Takes whatever the buttons leave. Without a minimum of nothing to ask for, a long
       query would push them out of the field instead of scrolling within it.

       The margin and the padding are the ones global.css gives every field, and both are
       spelled out here rather than inherited: the margin threw the row off centre, and the
       padding only needs to hold the text off the buttons beside it. */
    input {
        flex: 1 1 auto;
        min-width: 0;
        height: 100%;
        margin: 0;
        padding: 0 6px;
        border: none;
        outline: none;
        background: none;
        font: inherit;
        color: #212529;
    }

    /* The field has a clear button of its own, in the same place */
    input::-webkit-search-cancel-button {
        display: none;
    }

    /* Long lists scroll within the card rather than down past the bottom of the map */
    .results {
        margin-top: 6px;
        padding: 4px 0;
        background: #fff;
        border: 1px solid #444;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
        max-height: min(60vh, 420px);
        overflow-y: auto;
        overscroll-behavior: contain;
    }

    .message {
        margin: 0;
        padding: 10px 14px;
        color: #666;
    }

    .country {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 0;
        padding: 8px 14px 2px;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        color: #666;
    }

    .flag {
        font-size: 1rem;
    }

    .result {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        margin: 0;
        padding: 8px 14px;
        border: none;
        background: none;
        text-align: left;
        font: inherit;
        color: #212529;
        cursor: pointer;
    }

    .result:hover {
        background: #eee;
    }

    .result-icon {
        flex: 0 0 auto;
        width: 20px;
        text-align: center;
        color: #666;
    }

    /* The name runs to one line and is cut with an ellipsis, so that a long one doesn't
       stretch the card or wrap the municipality away from it. */
    .result-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .name {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .municipality {
        font-size: 0.8rem;
        color: #888;
    }
</style>
